import { SyntheticEvent, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  InputAdornment,
  TextField,
} from '@mui/material';
import * as state from '@/components/AssetLabelling/state';

type Props = {
  cuboidClassId: string | undefined;
  setCuboidClassId: (cuboidClassId: string) => void;
};

type Option = {
  id: string;
  tagName: string;
  hexColor: string;
};

const getOptionLabel = (option: Option) => {
  return option.tagName;
};

export const CuboidClassSelector = ({ cuboidClassId, setCuboidClassId }: Props) => {
  const cuboidClassById = useAtomValue(state.cuboidClassById);

  const selectedCuboidClass = useMemo(
    () => (cuboidClassId ? cuboidClassById?.[cuboidClassId] : undefined),
    [cuboidClassById, cuboidClassId]
  );

  const selectedTag = useMemo(
    () =>
      selectedCuboidClass
        ? {
            id: selectedCuboidClass.id,
            tagName: selectedCuboidClass.name,
            hexColor: `#${selectedCuboidClass.color.toString(16)}`,
          }
        : undefined,
    [selectedCuboidClass]
  );

  const allTags: Option[] = useMemo(() => {
    if (!cuboidClassById) return [];
    const allCuboidClasses = Object.values(cuboidClassById);
    return allCuboidClasses.map(({ id, name, color }) => ({
      id,
      tagName: name,
      hexColor: `#${color.toString(16)}`,
    }));
  }, [cuboidClassById]);

  const onCuboidClassChange = async (
    _event: SyntheticEvent,
    value: { id: string; tagName: string; isNewlyAdded?: boolean } | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption' && value) {
      setCuboidClassId(value.id);
    }
  };

  return (
    <Autocomplete
      sx={{
        width: '220px',
        color: '#fff',
        backgroundColor: '#fff',
        borderRadius: '4px',
        padding: '4px',
      }}
      fullWidth
      id="cuboid-class-selector"
      defaultValue={selectedTag}
      value={selectedTag}
      isOptionEqualToValue={(option, value) => value && option.id === value.id}
      options={allTags}
      getOptionLabel={getOptionLabel}
      disableListWrap
      disableClearable
      autoHighlight
      onChange={onCuboidClassChange}
      renderInput={(parameters) => (
        <TextField
          {...parameters}
          variant="standard"
          fullWidth
          placeholder="Select cuboid class..."
          InputProps={{
            ...parameters.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <Box
                    style={{
                      backgroundColor: selectedTag?.hexColor,
                      width: '16px',
                      height: '16px',
                      border: '1px solid #555',
                      margin: '5px 10px',
                    }}
                    key={`current-class-${selectedTag?.id}`}
                  />
                </InputAdornment>
                {parameters.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option: Option) => {
        return (
          <Box
            component="li"
            sx={{ '& > span': { mr: 2, flexShrink: 0 } }}
            {...props}
            key={option.id}
          >
            <Box
              style={{
                backgroundColor: option.hexColor,
                width: '16px',
                height: '16px',
                border: '1px solid #555',
                margin: '5px 10px',
              }}
            />
            {option.tagName}
          </Box>
        );
      }}
    />
  );
};
